import { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * ArgocdProjectPickerComponent
 *
 * Renders a dropdown of ArgoCD project Resources from the Backstage catalog,
 * filtered to only include projects whose `spec.system` matches the `system`
 * field selected elsewhere on the form.
 *
 * If no system is selected, the picker is disabled and prompts the user to
 * select a system first. Projects not belonging to the selected system are
 * never shown.
 *
 * Usage in template.yaml:
 *   argocd_project:
 *     title: ArgoCD Project
 *     type: string
 *     ui:field: ArgocdProjectPicker
 */
const ArgocdProjectPickerComponent = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  const [projects, setProjects] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Read the `system` field value set by EntityPicker (e.g. "system:default/petclinic")
  const systemRef: string | undefined = formContext?.formData?.system;

  // Derive the bare system name from the entity ref for catalog filtering
  const systemName = systemRef
    ? (() => {
        try {
          return parseEntityRef(systemRef, {
            defaultKind: 'System',
            defaultNamespace: 'default',
          }).name;
        } catch {
          return undefined;
        }
      })()
    : undefined;

  useEffect(() => {
    if (!systemName) {
      // No system selected — clear selection and show nothing
      setProjects([]);
      onChange(undefined as any);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    (async () => {
      try {
        const response = await catalogApi.getEntities({
          filter: [
            {
              kind: 'Resource',
              'spec.type': 'argocd-project',
              'spec.system': systemName,
            },
          ],
        });

        if (!cancelled) {
          setProjects(response.items);

          // If the currently selected project no longer belongs to the new system, clear it
          if (formData) {
            const stillValid = response.items.some(
              e => e.metadata.name === formData,
            );
            if (!stillValid) onChange(undefined as any);
          }
        }
      } catch (e: any) {
        if (!cancelled) setFetchError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [systemName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!systemName) {
    return (
      <FormControl fullWidth disabled>
        <InputLabel shrink>ArgoCD Project</InputLabel>
        <Select value="" displayEmpty>
          <MenuItem value="" disabled>
            <Typography variant="body2" color="textSecondary">
              Select a System above to see available ArgoCD projects
            </Typography>
          </MenuItem>
        </Select>
        <FormHelperText>
          Only projects belonging to the selected system will be shown
        </FormHelperText>
      </FormControl>
    );
  }

  if (loading) {
    return <CircularProgress size={24} />;
  }

  if (fetchError) {
    return (
      <FormControl fullWidth error>
        <InputLabel>ArgoCD Project</InputLabel>
        <Select value="">
          <MenuItem value="" disabled>Error loading projects</MenuItem>
        </Select>
        <FormHelperText>{fetchError}</FormHelperText>
      </FormControl>
    );
  }

  if (projects.length === 0) {
    return (
      <FormControl fullWidth error={required}>
        <InputLabel shrink>ArgoCD Project</InputLabel>
        <Select value="" displayEmpty>
          <MenuItem value="" disabled>
            <Typography variant="body2" color="textSecondary">
              No ArgoCD projects found for system: {systemName}
            </Typography>
          </MenuItem>
        </Select>
        <FormHelperText error={required}>
          {required
            ? `No ArgoCD projects are registered under system "${systemName}". Create one via the Manage ArgoCD template first.`
            : `No projects found for "${systemName}"`}
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth required={required} error={(rawErrors?.length ?? 0) > 0}>
      <InputLabel>ArgoCD Project</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        {projects.map(project => (
          <MenuItem key={project.metadata.name} value={project.metadata.name}>
            {project.metadata.title ?? project.metadata.name}
            {project.metadata.description && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginLeft: 8 }}
              >
                — {project.metadata.description}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {(rawErrors?.length ?? 0) > 0
          ? rawErrors![0]
          : `Showing projects for system: ${systemName}`}
      </FormHelperText>
    </FormControl>
  );
};

export const ArgocdProjectPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ArgocdProjectPicker',
    component: ArgocdProjectPickerComponent,
  }),
);
